'use client';

/**
 * Toaster — Virza-branded Sonner toast configuration.
 *
 * Upgrades over stock Sonner:
 *   - Frosted glass background (backdrop-blur) matching Virza's dark-mode identity
 *   - Left accent bar per severity (success=emerald, error=red, warning=amber, info=blue)
 *   - Brand-consistent border radius and typography
 *   - Custom icons from Lucide matching the rest of the design system
 */

import {
  CircleCheckIcon,
  InfoIcon,
  Loader2Icon,
  OctagonXIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = 'system' } = useTheme();

  return (
    <Sonner
      theme={theme as ToasterProps['theme']}
      className="toaster group"
      icons={{
        success: <CircleCheckIcon className="size-4 text-emerald-500" />,
        info: <InfoIcon className="size-4 text-blue-400" />,
        warning: <TriangleAlertIcon className="size-4 text-amber-400" />,
        error: <OctagonXIcon className="size-4 text-red-400" />,
        loading: <Loader2Icon className="size-4 animate-spin text-zinc-400" />,
      }}
      toastOptions={{
        classNames: {
          toast:
            'group-[.toaster]:border-border/50 group-[.toaster]:shadow-lg group-[.toaster]:backdrop-blur-lg group-[.toaster]:overflow-hidden',
          title: 'group-[.toast]:text-foreground group-[.toast]:font-semibold',
          description: 'group-[.toast]:text-muted-foreground group-[.toast]:text-[13px]',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:text-xs group-[.toast]:font-medium',
          cancelButton:
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:text-xs',
          // Per-type accent via data attributes (styled in globals.css)
          success: 'group-[.toaster]:!border-l-[3px] group-[.toaster]:!border-l-emerald-500/70',
          error: 'group-[.toaster]:!border-l-[3px] group-[.toaster]:!border-l-red-500/70',
          warning: 'group-[.toaster]:!border-l-[3px] group-[.toaster]:!border-l-amber-500/70',
          info: 'group-[.toaster]:!border-l-[3px] group-[.toaster]:!border-l-blue-500/70',
        },
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      {...props}
    />
  );
};

export { Toaster };
